import { Center, ConditionalRendering, Loader, Text } from "@marketplace/ui";
import { getRouteApi } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useTranslation } from "react-i18next";

import { Pagination } from "@/components";
import { usePermissions } from "@/hooks/use-permissions";

import { claimIntegrationStatusModalAtom } from "../../atoms/claim-integration-status-modal-atom";
import { ClaimDeleteModal } from "../../components/claim-delete-modal";
import { ClaimHistoryFilter } from "../../components/claim-history-filter";
import { ClaimHistoryReviewModal } from "../../components/claim-history-review-modal";
import { ClaimHistoryTable } from "../../components/claim-history-table";
import { ClaimIntegrationStatusModal } from "../../components/claim-integration-status-modal";
import { ClaimOrderSubmitModal } from "../../components/claim-order-submit-modal";
import { useGetClaimHistoryBadgeCountQuery } from "../../hooks/queries/use-get-claim-history-badge-count-query";
import { useGetClaimHistoryListQuery } from "../../hooks/queries/use-get-claim-history-list-query";

const route = getRouteApi("/disbursement/claim-history/");

const NoClaimHistoryIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="120"
    height="120"
    viewBox="0 0 24 24"
    fill="none"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="#2F7DC8"
      strokeWidth="2"
      fill="none"
    />
    <line
      x1="4.93"
      y1="4.93"
      x2="19.07"
      y2="19.07"
      stroke="#2F7DC8"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const NoClaimHistory = () => {
  const { t } = useTranslation("order", {
    keyPrefix: "claimHistoryPage.emptyState"
  });
  return (
    <div className="flex flex-col justify-center items-center gap-y-4 mt-[56px]">
      <NoClaimHistoryIcon />
      <div className="text-center max-w-sm">
        <Text
          type="heading"
          size="xl"
          className="mb-2 text-fjord-grey-500"
          weight="semibold"
        >
          {t("title")}
        </Text>
        <Text type="paragraph" className="text-fjord-grey-300">
          {t("description")}
        </Text>
      </div>
    </div>
  );
};

export const ClaimHistoryListPage = () => {
  const { page, q, status, sort } = route.useSearch();
  const navigate = route.useNavigate();
  const { t } = useTranslation("order", { keyPrefix: "claimHistoryPage" });

  const { data, isLoading } = useGetClaimHistoryListQuery({
    queryParams: {
      page,
      limit: 10,
      q,
      status,
      sort
    }
  });

  const { data: badgeCountData } = useGetClaimHistoryBadgeCountQuery();

  const records = data?.data ?? [];
  const metadata = data?.metadata;
  const { isSeller } = usePermissions();
  const claimIntegrationStatusModal = useAtomValue(
    claimIntegrationStatusModalAtom
  );
  const resetClaimIntegrationStatusModal = useResetAtom(
    claimIntegrationStatusModalAtom
  );

  // only count for actionable claim status
  const countsSuperAdmin = {
    review: badgeCountData?.data?.count ?? 0
  };

  const countsSeller = {
    reject: badgeCountData?.data?.count ?? 0
  };

  return (
    <>
      <ClaimHistoryReviewModal />
      <ClaimOrderSubmitModal />
      <ClaimDeleteModal />
      <ClaimIntegrationStatusModal
        opened={claimIntegrationStatusModal.visible}
        onClose={resetClaimIntegrationStatusModal}
        publicId={claimIntegrationStatusModal.publicId}
      />

      <div className="flex justify-between items-center mb-6">
        <div>
          <Text type="heading" size="l" className="text-humble-blue-500">
            {t("title")}
          </Text>
          <Text className="text-fjord-grey-400">{t("description")}</Text>
        </div>
      </div>

      <ClaimHistoryFilter counts={isSeller ? countsSeller : countsSuperAdmin} />

      <ConditionalRendering condition={isLoading}>
        <Center className="my-20">
          <Loader />
        </Center>
      </ConditionalRendering>

      <ConditionalRendering condition={!isLoading && records.length === 0}>
        <NoClaimHistory />
      </ConditionalRendering>

      <ConditionalRendering condition={!isLoading && records.length > 0}>
        <div className="space-y-6">
          <ClaimHistoryTable records={records} page={page} />
          <Pagination
            currentPageTotal={records.length}
            totalData={metadata?.total}
            total={metadata?.totalPage ?? 0}
            value={metadata?.page}
            onChange={(value) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  page: value
                })
              })
            }
          />
        </div>
      </ConditionalRendering>
    </>
  );
};
